import i18n from '@/i18n/translations';

// Internal order status (our system)
export enum OrderStatus {
  PENDING = 'pending',
  CREATED = 'created',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  ERROR = 'error',
}

// Mercado Libre shipment status
export enum ShipmentStatus {
  HANDLING = 'handling',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  NOT_DELIVERED = 'not_delivered',
}

export const getOrderStatusLabel = (status: string) => {
  return i18n.t(`commonGeneral:status.${status}`, { defaultValue: status });
};

export const getOrderStatusBg = (status: string) => {
  switch (status) {
    case OrderStatus.PENDING:
      return '#FFF9E6';
    case OrderStatus.CREATED:
      return '#D1ECF1';
    case OrderStatus.DELIVERED:
      return '#D6F2DF';
    case OrderStatus.CANCELLED:
      return '#F6DDDC';
    case OrderStatus.ERROR:
      return '#F8D7DA';
    default:
      return '#F8F9FA';
  }
};

export const getOrderStatusColor = (status: string) => {
  switch (status) {
    case OrderStatus.PENDING:
      return '#E67E22';
    case OrderStatus.CREATED:
      return '#3498DB';
    case OrderStatus.DELIVERED:
      return '#27AE60';
    case OrderStatus.CANCELLED:
      return '#C0392B';
    case OrderStatus.ERROR:
      return '#E74C3C';
    default:
      return '#95A5A6';
  }
};

export const getShipmentStatusBg = (status: string) => {
  switch (status) {
    case ShipmentStatus.HANDLING:
      return '#FFF3CD';
    case ShipmentStatus.SHIPPED:
      return '#D1ECF1';
    case ShipmentStatus.DELIVERED:
      return '#D6F2DF';
    case ShipmentStatus.CANCELLED:
      return '#F6DDDC';
    case ShipmentStatus.NOT_DELIVERED:
      return '#F8D7DA';
    default:
      return '#F8F9FA';
  }
};

export const getShipmentStatusColor = (status: string) => {
  switch (status) {
    case ShipmentStatus.HANDLING:
      return '#E67E22';
    case ShipmentStatus.SHIPPED:
      return '#3498DB';
    case ShipmentStatus.DELIVERED:
      return '#27AE60';
    case ShipmentStatus.CANCELLED:
      return '#C0392B';
    case ShipmentStatus.NOT_DELIVERED:
      return '#E74C3C';
    default:
      return '#95A5A6';
  }
};

// ML statusOrder (payment statuses from Mercado Libre)
export const getStatusOrderLabel = (status: string) => {
  return i18n.t(`commonGeneral:status.${status}`, { defaultValue: status });
};

export const getStatusOrderBg = (status: string) => {
  switch (status) {
    case 'confirmed':
      return '#FFF9E6';
    case 'payment_required':
      return '#FFF3CD';
    case 'payment_in_process':
      return '#D1ECF1';
    case 'partially_paid':
      return '#FFE5CC';
    case 'paid':
      return '#D6F2DF';
    case 'partially_refunded':
      return '#E8DAEF';
    case 'pending_cancel':
      return '#F8D7DA';
    case 'cancelled':
      return '#F6DDDC';
    default:
      return '#F8F9FA';
  }
};

export const getStatusOrderColor = (status: string) => {
  switch (status) {
    case 'confirmed':
      return '#F39C12';
    case 'payment_required':
      return '#E67E22';
    case 'payment_in_process':
      return '#3498DB';
    case 'partially_paid':
      return '#E67E22';
    case 'paid':
      return '#27AE60';
    case 'partially_refunded':
      return '#8E44AD';
    case 'pending_cancel':
      return '#E74C3C';
    case 'cancelled':
      return '#C0392B';
    default:
      return '#95A5A6';
  }
};

// ML product status
export const getProductStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    active: 'Activo',
    paused: 'Pausado',
    closed: 'Cerrado',
    under_review: 'En revisión',
  };
  return labels[status] ?? status;
};

export const getProductStatusBg = (status: string) => {
  switch (status) {
    case 'active':
      return '#D6F2DF';
    case 'paused':
      return '#FEF0E0';
    case 'closed':
      return '#FDEDEC';
    case 'under_review':
      return '#D6EAF8';
    default:
      return '#F8F9FA';
  }
};

export const getProductStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return '#27AE60';
    case 'paused':
      return '#E67E22';
    case 'closed':
      return '#C0392B';
    case 'under_review':
      return '#2980B9';
    default:
      return '#95A5A6';
  }
};

// ML shipping mode
export const getShippingModeLabel = (mode: string) => {
  const labels: Record<string, string> = {
    me1: 'ME1',
    me2: 'ME2',
    custom: 'Custom',
    not_specified: 'No especificado',
    default: 'Default',
  };
  return labels[mode] ?? mode;
};

export const getShippingModeBg = (mode: string) => {
  switch (mode) {
    case 'me1':
      return '#E3F2FD';
    case 'me2':
      return '#F3E5F5';
    case 'custom':
      return '#FFF3E0';
    case 'not_specified':
      return '#ECEFF1';
    case 'default':
      return '#F5F5F5';
    default:
      return '#F8F9FA';
  }
};

export const getShippingModeColor = (mode: string) => {
  switch (mode) {
    case 'me1':
      return '#1565C0';
    case 'me2':
      return '#6A1B9A';
    case 'custom':
      return '#E65100';
    case 'not_specified':
      return '#546E7A';
    case 'default':
      return '#757575';
    default:
      return '#95A5A6';
  }
};

export const filtersConfig = [
  {
    filterName: 'status',
    items: [
      { value: 'confirmed' },
      { value: 'payment_required' },
      { value: 'payment_in_process' },
      { value: 'partially_paid' },
      { value: 'paid' },
      { value: 'partially_refunded' },
      { value: 'pending_cancel' },
      { value: 'cancelled' },
    ],
  },
];

export const orderStatusFilters = [
  { value: OrderStatus.PENDING },
  { value: OrderStatus.CREATED },
  { value: OrderStatus.DELIVERED },
  { value: OrderStatus.CANCELLED },
  { value: OrderStatus.ERROR },
];
